import { useCallback, useEffect } from "react";
import { useAppDispatch, useAppParams, useAppSelector } from "@app/hooks";
import {
  setProjectAreaDefined,
  selectProjectAreaStatusVersion,
  selectProjectAreaId
} from "@features/project/projectSlice";
import { fetchProjectExternalData } from "@app/services/helperBackendApiService";

/**
 * Fetches the project area status from the helper backend and stores it in Redux.
 * Should be called at the ProjectContainer level so it runs once per project.
 *
 * - State is cleared automatically when clearProject is dispatched.
 * - Dispatching `invalidateProjectAreaStatus` bumps the version counter,
 *   which triggers an automatic re-fetch via the useEffect dependency.
 * - The returned `refetch` callback can be used for imperative re-fetches.
 */
export const useProjectAreaStatus = () => {
  const dispatch = useAppDispatch();
  const { rawProjectId } = useAppParams();
  const version = useAppSelector(selectProjectAreaStatusVersion);
  const projectAreaId = useAppSelector(selectProjectAreaId);

  const fetchStatus = useCallback(
    (projectId: string) => {
      fetchProjectExternalData(projectId)
        .then((data) => {
          const isDefined = data.projectAreaGeoJSON !== null;
          dispatch(setProjectAreaDefined({ defined: isDefined, area: data.projectAreaGeoJSON, id: projectId }));
        })
        .catch(() => {
          dispatch(setProjectAreaDefined({ defined: false, area: null, id: null }));
          // Silently fail — banner won't render in an error state
        });
    },
    [dispatch]
  );

  useEffect(() => {
    if (!rawProjectId) return;
    if (projectAreaId === rawProjectId) return;
    dispatch(setProjectAreaDefined({ defined: null, area: null, id: null }));
    fetchStatus(rawProjectId);
  }, [rawProjectId, version, fetchStatus, dispatch, projectAreaId]);
};
